/**
 * useAnalyticsData — three hooks for the MVP Analytics dashboards.
 *
 * Each hook wraps one `/api/analytics/*` endpoint, pipes `useDashboardFilters`
 * URL state through `buildAnalyticsQuery`, and returns `{ data, loading,
 * error, refetch }`. Filter changes (URL changes) auto-refetch. No caching
 * beyond React state — keeps the surface small; React Query / SWR can be
 * layered later if needed.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  buildAnalyticsQuery,
  getActiveUsersAnalytics,
  getActiveEndpointsAnalytics,
  getOverviewRaAnalytics,
} from '@/services/api';
import { useDashboardFilters } from './useDashboardFilters';
import type {
  ActiveUsersDataset,
  ActiveEndpointsDataset,
  OverviewRaDataset,
} from '@/types/analytics';

interface UseAnalyticsResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

function useAnalyticsFetcher<T>(
  fetcher: (qs: string) => Promise<T>,
  queryKey: string
): UseAnalyticsResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcher(queryKey)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch analytics data'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, queryKey, tick]);

  return { data, loading, error, refetch };
}

export function useActiveUsersData(): UseAnalyticsResult<ActiveUsersDataset> {
  const { dateRange, orgs, nodes } = useDashboardFilters();
  const qs = useMemo(() => buildAnalyticsQuery({ dateRange, orgs, nodes }), [dateRange, orgs, nodes]);
  return useAnalyticsFetcher(getActiveUsersAnalytics, qs);
}

export function useActiveEndpointsData(): UseAnalyticsResult<ActiveEndpointsDataset> {
  const { dateRange, orgs, nodes } = useDashboardFilters();
  const qs = useMemo(() => buildAnalyticsQuery({ dateRange, orgs, nodes }), [dateRange, orgs, nodes]);
  return useAnalyticsFetcher(getActiveEndpointsAnalytics, qs);
}

export function useOverviewRaData(): UseAnalyticsResult<OverviewRaDataset> {
  const { dateRange, orgs, nodes } = useDashboardFilters();
  const qs = useMemo(() => buildAnalyticsQuery({ dateRange, orgs, nodes }), [dateRange, orgs, nodes]);
  return useAnalyticsFetcher(getOverviewRaAnalytics, qs);
}
